import { useCallback, useEffect, useRef, useState } from "react";
import type { ControlIntent, SessionState, User } from "../types";

type SocketStatus = "connecting" | "connected" | "disconnected";

export function useSessionSocket(user: User | null) {
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const sequence = useRef(0);
  const [state, setState] = useState<SessionState | null>(null);
  const [status, setStatus] = useState<SocketStatus>("disconnected");
  const [lastIntent, setLastIntent] = useState<ControlIntent | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let closedByEffect = false;
    let retryMs = 500;

    const connect = () => {
      setStatus("connecting");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        retryMs = 500;
        setStatus("connected");
      });

      socket.addEventListener("message", (event) => {
        const message = JSON.parse(String(event.data)) as {
          type: string;
          state?: SessionState;
          code?: string;
          intent?: "start" | "stop";
          hold?: ControlIntent["hold"];
          holdId?: string;
          reason?: string;
        };
        if (message.type === "snapshot" && message.state) {
          setState(message.state);
          return;
        }
        if (message.type === "control:intent" && message.intent) {
          sequence.current += 1;
          setLastIntent({
            type: "control:intent",
            intent: message.intent,
            hold: message.hold,
            holdId: message.holdId,
            reason: message.reason,
            sequence: sequence.current
          });
          return;
        }
        if (message.type === "error") {
          setLastError(message.code ?? "request_failed");
        }
      });

      socket.addEventListener("close", () => {
        setStatus("disconnected");
        socketRef.current = null;
        if (!closedByEffect) {
          reconnectTimer.current = window.setTimeout(connect, retryMs);
          retryMs = Math.min(retryMs * 2, 5_000);
        }
      });
    };

    connect();
    return () => {
      closedByEffect = true;
      if (reconnectTimer.current) window.clearTimeout(reconnectTimer.current);
      socketRef.current?.close(1000);
      socketRef.current = null;
      setState(null);
      setLastIntent(null);
    };
  }, [user]);

  const send = useCallback((message: Record<string, unknown>) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setLastError("relay_disconnected");
      return false;
    }
    socket.send(JSON.stringify(message));
    return true;
  }, []);

  return {
    state,
    status,
    lastIntent,
    lastError,
    clearError: () => setLastError(null),
    send
  };
}
